import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";


const API = "https://bmtvendorvendorfinal.onrender.com/api/hotel-booking";
const HOTELS_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/hotels";

const PAGE_SIZE = 100; // backend max
const MAX_PAGES = 10; // 1000 bookings tak load karega

const getToken = () => {
  const t = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  return t && t !== "null" && t !== "undefined" ? t : null;
};
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

/* ---------- status config ---------- */
const STATUS = {
  PENDING: { label: "Needs your reply", c: "#b45309", bg: "#fef3c7", bar: "#f59e0b" },
  CONFIRMED: { label: "Confirmed", c: "#166534", bg: "#dcfce7", bar: "#16a34a" },
  CHECKED_IN: { label: "Checked in", c: "#1d4ed8", bg: "#dbeafe", bar: "#3b82f6" },
  CHECKED_OUT: { label: "Checked out", c: "#0e7490", bg: "#cffafe", bar: "#06b6d4" },
  COMPLETED: { label: "Completed", c: "#4b5563", bg: "#f3f4f6", bar: "#9ca3af" },
  CANCELLED: { label: "Cancelled", c: "#b91c1c", bg: "#fee2e2", bar: "#ef4444" },
  NO_SHOW: { label: "No show", c: "#374151", bg: "#e5e7eb", bar: "#6b7280" },
};

/* tab key, label, kaun se bookingStatus us tab me aayenge (null = sab) */
const TABS = [
  ["ALL", "All", null],
  ["PENDING", "Pending", ["PENDING"]],
  ["CONFIRMED", "Confirmed", ["CONFIRMED"]],
  ["CHECKED_IN", "In house", ["CHECKED_IN"]],
  ["PAST", "Past stays", ["CHECKED_OUT", "COMPLETED"]],
  ["CLOSED", "Cancelled", ["CANCELLED", "NO_SHOW"]],
];

const CANCEL_REASONS = [
  "Rooms not available",
  "Property under maintenance",
  "Dates not suitable",
  "Guest asked to cancel",
];

const ID_TYPES = ["Aadhaar", "Passport", "Driving License", "Voter ID", "PAN", "Other"];

/* ---------- date helpers (booking dates UTC midnight par store hoti hain) ---------- */
const dayKey = (d) => new Date(d).toISOString().slice(0, 10);
const localToday = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
const diffDays = (a, b) => Math.round((Date.parse(a) - Date.parse(b)) / 864e5);
const fmtDay = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", timeZone: "UTC" });
const fmtMon = (d) => new Date(d).toLocaleDateString("en-IN", { month: "short", timeZone: "UTC" });
const fmtWeek = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
const fmtFull = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "—";
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const pretty = (s) => String(s || "—").replace(/_/g, " ").toLowerCase();

const whenLabel = (b, today) => {
  if (!b.checkIn || !b.checkOut) return "";
  const inKey = dayKey(b.checkIn);
  const outKey = dayKey(b.checkOut);
  if (b.bookingStatus === "CHECKED_IN") {
    const d = diffDays(outKey, today);
    return d <= 0 ? "Check-out due today" : `Leaves in ${d} day(s)`;
  }
  if (!["PENDING", "CONFIRMED"].includes(b.bookingStatus)) return "";
  const d = diffDays(inKey, today);
  if (d === 0) return "Arrives today";
  if (d === 1) return "Arrives tomorrow";
  if (d > 1) return `Arrives in ${d} days`;
  return "Check-in date passed";
};

const hotelIdOf = (b) => (b.hotel && b.hotel._id) || b.hotel || "";

export default function VendorHotelBookings() {
  const [bookings, setBookings] = useState([]);
  const [totalOnServer, setTotalOnServer] = useState(0);
  const [hotels, setHotels] = useState([]);
  const [tab, setTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [hotelFilter, setHotelFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modal, setModal] = useState(null); // { type: "cancel" | "checkin" | "pay" | "view", booking }
  const [reason, setReason] = useState("");
  const [txn, setTxn] = useState("");
  const [idForm, setIdForm] = useState({ idType: "Aadhaar", idNumber: "", idVerified: true, remarks: "" });

  const today = localToday();

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3500);
  };

  /* ---------- loaders ---------- */
  const loadBookings = useCallback(async () => {
    if (!getToken()) {
      window.location.href = "/vendor-login";
      return;
    }
    setLoading(true);
    try {
      const first = await axios.get(API, { ...authHeaders(), params: { page: 1, limit: PAGE_SIZE } });
      let all = first.data.bookings || [];
      const pages = Math.min(first.data.pages || 1, MAX_PAGES);

      for (let p = 2; p <= pages; p += 1) {
        const next = await axios.get(API, { ...authHeaders(), params: { page: p, limit: PAGE_SIZE } });
        all = all.concat(next.data.bookings || []);
      }

      setBookings(all);
      setTotalOnServer(first.data.total || all.length);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Bookings load nahi hui");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    if (!getToken()) return;
    axios
      .get(HOTELS_API, authHeaders())
      .then((r) => {
        const d = r.data;
        setHotels(
          Array.isArray(d) ? d
          : Array.isArray(d?.hotels) ? d.hotels
          : Array.isArray(d?.data) ? d.data
          : Array.isArray(d?.data?.hotels) ? d.data.hotels : []
        );
      })
      .catch(() => {}); // hotel filter optional hai
  }, []);

  /* ---------- actions ---------- */
  const run = async (b, path, body = {}) => {
    setBusyId(b._id);
    setError("");
    try {
      const res = await axios.put(`${API}/${b._id}/${path}`, body, authHeaders());
      flash(res.data?.message || "Done");
      await loadBookings();
      return true;
    } catch (e) {
      // e.g. confirm par "Only N room(s) available..." yahin dikhega
      setError(e.response?.data?.message || e.message || "Action failed");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const openModal = (type, booking) => {
    setReason("");
    setTxn("");
    setIdForm({ idType: "Aadhaar", idNumber: "", idVerified: true, remarks: "" });
    setModal({ type, booking });
  };

  const onAction = (b, action) => {
    if (action === "confirm") return run(b, "confirm");
    if (action === "check-out") return run(b, "check-out");
    if (action === "cancel") return openModal("cancel", b);
    if (action === "check-in") return openModal("checkin", b);
    return null;
  };

  const submitCancel = async () => {
    const { booking } = modal;
    setModal(null);
    await run(booking, "cancel", { reason: reason.trim() || "Cancelled by vendor" });
  };

  const submitCheckIn = async () => {
    const { booking } = modal;
    setModal(null);
    await run(booking, "check-in", idForm);
  };

  const submitPay = async () => {
    const { booking } = modal;
    setModal(null);
    await run(booking, "payment", { transactionId: txn.trim() });
  };

  /* ---------- derived ---------- */
  const counts = useMemo(() => {
    const c = { ALL: bookings.length };
    TABS.forEach(([key, , statuses]) => {
      if (statuses) c[key] = bookings.filter((b) => statuses.includes(b.bookingStatus)).length;
    });
    return c;
  }, [bookings]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statuses = (TABS.find((t) => t[0] === tab) || [])[2];

    return bookings
      .filter((b) => !statuses || statuses.includes(b.bookingStatus))
      .filter((b) => !hotelFilter || String(hotelIdOf(b)) === hotelFilter)
      .filter(
        (b) =>
          !q ||
          [b.guest?.name, b.guest?.phone, b.guest?.email, b.bookingNumber, b.hotel?.hotelName, b.room?.roomName, b.roomName]
            .some((x) => String(x || "").toLowerCase().includes(q))
      )
      .sort((a, b) => {
        const pa = a.bookingStatus === "PENDING" ? 0 : 1;
        const pb = b.bookingStatus === "PENDING" ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return new Date(b.createdAt || b.checkIn) - new Date(a.createdAt || a.checkIn);
      });
  }, [bookings, tab, search, hotelFilter]);

  const actionsFor = (b) => {
    switch (b.bookingStatus) {
      case "PENDING": return [["confirm", "Accept", "primary"], ["cancel", "Reject", "danger"]];
      case "CONFIRMED": return [["check-in", "Check in", "primary"], ["cancel", "Cancel", "danger"]];
      case "CHECKED_IN": return [["check-out", "Check out", "primary"]];
      default: return [];
    }
  };

  const canMarkPaid = (b) =>
    b.paymentStatus === "PENDING" && !["CANCELLED", "NO_SHOW"].includes(b.bookingStatus);

  /* ============================================================ RENDER */
  return (
    <div style={S.page} className="hbk-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes hbkspin { to { transform: rotate(360deg); } }
        @keyframes hbkpop { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .hbk-page button:focus-visible, .hbk-page input:focus-visible, .hbk-page textarea:focus-visible, .hbk-page select:focus-visible { outline: 2px solid #166534; outline-offset: 2px; }
        .hbk-page input:focus, .hbk-page textarea:focus, .hbk-page select:focus { border-color: #166534 !important; }
        .hbk-card { transition: box-shadow .15s; }
        .hbk-card:hover { box-shadow: 0 6px 22px rgba(0,0,0,.08) !important; }
        .hbk-tabs { scrollbar-width: none; }
        .hbk-tabs::-webkit-scrollbar { display: none; }
        .hbk-grid { display: grid; grid-template-columns: 170px minmax(0,1.5fr) minmax(0,1fr) 150px; gap: 20px; align-items: center; }
        @media (max-width: 960px) { .hbk-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 560px) { .hbk-grid { grid-template-columns: 1fr; } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
      `}</style>

      {success && <div style={S.toast("#166534")}>{success}</div>}
      {error && (
        <div style={S.toast("#dc2626")}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={S.toastX} aria-label="Dismiss">✕</button>
        </div>
      )}

      <div style={S.header}>
        <div>
          <h1 style={S.title}>Hotel Bookings</h1>
          <p style={S.subtitle}>
            {counts.PENDING
              ? `${counts.PENDING} request${counts.PENDING > 1 ? "s" : ""} waiting for your reply`
              : "Guest requests and stays for your hotels"}
          </p>
        </div>
        <button onClick={loadBookings} style={S.ghostBtn} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div style={S.toolbar}>
        <div className="hbk-tabs" style={S.tabs} role="tablist">
          {TABS.map(([k, l]) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              style={{ ...S.tab, ...(tab === k ? S.tabOn : {}) }}
            >
              {l}
              <span
                style={{
                  ...S.count,
                  ...(tab === k ? S.countOn : {}),
                  ...(k === "PENDING" && counts.PENDING && tab !== k ? S.countHot : {}),
                }}
              >
                {counts[k] || 0}
              </span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: "1 1 320px", justifyContent: "flex-end" }}>
          {hotels.length > 1 && (
            <select value={hotelFilter} onChange={(e) => setHotelFilter(e.target.value)} style={S.select} aria-label="Filter by hotel">
              <option value="">All hotels</option>
              {hotels.map((h) => (
                <option key={h._id} value={h._id}>{h.hotelName}</option>
              ))}
            </select>
          )}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guest, phone, booking no. or room"
            style={S.search}
            aria-label="Search bookings"
          />
        </div>
      </div>

      {totalOnServer > bookings.length && (
        <div style={S.note}>
          Showing latest {bookings.length} of {totalOnServer} bookings.
        </div>
      )}

      {loading && bookings.length === 0 ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : list.length === 0 ? (
        <div style={S.empty}>
          <div style={S.emptyIcon}>▤</div>
          <p style={{ fontSize: 17, fontWeight: 600, color: "#374151", margin: 0 }}>
            {bookings.length === 0 ? "No bookings yet" : "No bookings match this view"}
          </p>
          <p style={{ color: "#9ca3af", marginTop: 8, fontSize: 14 }}>
            {bookings.length === 0
              ? "Open dates in Room Inventory so guests can start booking."
              : "Try another tab or clear the search."}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {list.map((b) => {
            const st = STATUS[b.bookingStatus] || STATUS.COMPLETED;
            const actions = actionsFor(b);
            const when = whenLabel(b, today);
            const busy = busyId === b._id;
            const paid = String(b.paymentStatus).toUpperCase() === "PAID";
            const children = Number(b.guest?.children || 0);
            const adults = Number(b.guest?.adults || 1);

            return (
              <article
                key={b._id}
                className="hbk-card"
                style={{ ...S.card, borderLeft: `4px solid ${st.bar}`, animation: "hbkpop .25s ease" }}
              >
                <div className="hbk-grid">
                  {/* dates */}
                  <div style={S.dates}>
                    <DateBlock d={b.checkIn} />
                    <div style={S.nightsLine}>
                      <span style={S.nightsText}>{b.nights}n</span>
                    </div>
                    <DateBlock d={b.checkOut} />
                  </div>

                  {/* guest + hotel */}
                  <div style={{ minWidth: 0 }}>
                    <div style={S.guest}>{b.guest?.name || "Guest"}</div>
                    {b.guest?.phone && <a href={`tel:${b.guest.phone}`} style={S.phone}>{b.guest.phone}</a>}
                    <div style={S.prop}>{b.hotel?.hotelName || "—"}</div>
                    <div style={S.muted}>
                      {b.room?.roomName || b.roomName || "—"}
                      {b.hotel?.city ? `, ${b.hotel.city}` : ""}
                    </div>
                    <div style={S.id}>#{b.bookingNumber}</div>
                  </div>

                  {/* party */}
                  <div>
                    <div style={S.metaRow}><b>{b.roomsBooked}</b> room{b.roomsBooked > 1 ? "s" : ""}</div>
                    <div style={S.metaRow}>
                      <b>{adults}</b> adult{adults > 1 ? "s" : ""}
                      {children ? <>, <b>{children}</b> child{children > 1 ? "ren" : ""}</> : null}
                    </div>
                    <div style={{ ...S.muted, marginTop: 8 }}>
                      {b.inventoryDeducted ? "Rooms held for guest" : "Rooms not held yet"}
                    </div>
                    {b.guest?.specialRequest && (
                      <div style={{ ...S.muted, marginTop: 4, textTransform: "none" }} title={b.guest.specialRequest}>
                        “{b.guest.specialRequest.length > 40 ? `${b.guest.specialRequest.slice(0, 40)}…` : b.guest.specialRequest}”
                      </div>
                    )}
                  </div>

                  {/* money */}
                  <div style={{ textAlign: "right" }}>
                    <div style={S.amount}>{money(b.totalAmount)}</div>
                    <span style={{ ...S.pay, ...(paid ? S.payPaid : {}) }}>{pretty(b.paymentStatus)}</span>
                    <div style={{ ...S.muted, marginTop: 6 }}>{pretty(b.paymentMethod)}</div>
                  </div>
                </div>

                <div style={S.foot}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ ...S.badge, background: st.bg, color: st.c }}>{st.label}</span>
                    {when && (
                      <span style={{ ...S.when, ...(when.includes("today") || when.includes("due") ? S.whenHot : {}) }}>
                        {when}
                      </span>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => openModal("view", b)}>Details</button>
                    {canMarkPaid(b) && (
                      <button
                        disabled={busy}
                        onClick={() => openModal("pay", b)}
                        style={{ ...S.btn, ...S.btnAmber, opacity: busy ? 0.6 : 1 }}
                      >
                        Mark paid
                      </button>
                    )}
                    {actions.map(([action, label, kind]) => (
                      <button
                        key={action}
                        disabled={busy}
                        onClick={() => onAction(b, action)}
                        style={{ ...S.btn, ...(kind === "primary" ? S.btnPrimary : S.btnDanger), opacity: busy ? 0.6 : 1 }}
                      >
                        {busy ? "Please wait…" : label}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ===== cancel / reject modal ===== */}
      {modal?.type === "cancel" && (
        <Overlay onClose={() => setModal(null)}>
          <h3 style={S.modalTitle}>
            {modal.booking.bookingStatus === "CONFIRMED" ? "Cancel this booking?" : "Reject this request?"}
          </h3>
          <p style={{ ...S.muted, fontSize: 14, margin: "6px 0 14px", textTransform: "none" }}>
            {modal.booking.guest?.name}, {fmtDay(modal.booking.checkIn)} {fmtMon(modal.booking.checkIn)} to{" "}
            {fmtDay(modal.booking.checkOut)} {fmtMon(modal.booking.checkOut)}. Booked rooms inventory me wapas aa jayenge.
          </p>
          {modal.booking.paymentStatus === "PAID" && (
            <div style={S.warn}>
              Payment PAID hai, cancel karne par {money(modal.booking.totalAmount)} REFUNDED mark ho jayega.
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {CANCEL_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setReason(r)} style={{ ...S.chip, ...(reason === r ? S.chipOn : {}) }}>
                {r}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Add a reason (optional)"
            style={S.textarea}
          />
          <div style={S.modalFoot}>
            <button onClick={() => setModal(null)} style={S.ghostBtn}>Keep booking</button>
            <button onClick={submitCancel} style={{ ...S.btn, ...S.btnDangerSolid }}>
              {modal.booking.bookingStatus === "CONFIRMED" ? "Cancel booking" : "Reject request"}
            </button>
          </div>
        </Overlay>
      )}

      {/* ===== check-in modal ===== */}
      {modal?.type === "checkin" && (
        <Overlay onClose={() => setModal(null)}>
          <h3 style={S.modalTitle}>Check in {modal.booking.guest?.name}</h3>
          <p style={{ ...S.muted, fontSize: 14, margin: "6px 0 14px", textTransform: "none" }}>
            Guest ka ID verify karke details save karo.
          </p>
          <label style={S.label}>ID type</label>
          <select
            style={S.input}
            value={idForm.idType}
            onChange={(e) => setIdForm({ ...idForm, idType: e.target.value })}
          >
            {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label style={{ ...S.label, marginTop: 12 }}>ID number</label>
          <input
            style={S.input}
            value={idForm.idNumber}
            onChange={(e) => setIdForm({ ...idForm, idNumber: e.target.value })}
            placeholder="e.g. 1234 5678 9012"
          />
          <label style={S.check}>
            <input
              type="checkbox"
              checked={idForm.idVerified}
              onChange={(e) => setIdForm({ ...idForm, idVerified: e.target.checked })}
            />
            ID verified
          </label>
          <label style={S.label}>Remarks</label>
          <textarea
            style={S.textarea}
            rows={2}
            value={idForm.remarks}
            onChange={(e) => setIdForm({ ...idForm, remarks: e.target.value })}
          />
          <div style={S.modalFoot}>
            <button onClick={() => setModal(null)} style={S.ghostBtn}>Cancel</button>
            <button onClick={submitCheckIn} style={{ ...S.btn, ...S.btnPrimary }}>Check in guest</button>
          </div>
        </Overlay>
      )}

      {/* ===== mark paid modal ===== */}
      {modal?.type === "pay" && (
        <Overlay onClose={() => setModal(null)}>
          <h3 style={S.modalTitle}>Mark payment received</h3>
          <p style={{ ...S.muted, fontSize: 14, margin: "6px 0 14px", textTransform: "none" }}>
            {modal.booking.guest?.name} · {money(modal.booking.totalAmount)} · {pretty(modal.booking.paymentMethod)}
          </p>
          <label style={S.label}>Transaction ID (optional)</label>
          <input
            style={S.input}
            value={txn}
            onChange={(e) => setTxn(e.target.value)}
            placeholder="UPI ref / receipt no."
          />
          <div style={S.modalFoot}>
            <button onClick={() => setModal(null)} style={S.ghostBtn}>Cancel</button>
            <button onClick={submitPay} style={{ ...S.btn, ...S.btnPrimary }}>Mark as paid</button>
          </div>
        </Overlay>
      )}

      {/* ===== details modal ===== */}
      {modal?.type === "view" && <ViewModal b={modal.booking} onClose={() => setModal(null)} />}
    </div>
  );
}

/* ============================================================
   SMALL COMPONENTS
============================================================ */
function Overlay({ children, onClose, wide }) {
  return (
    <div style={S.overlay} onClick={onClose}>
      <div
        style={{ ...S.modal, maxWidth: wide ? 560 : 480 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}

function DateBlock({ d }) {
  return (
    <div style={S.dateBlock}>
      <div style={S.dateWeek}>{fmtWeek(d)}</div>
      <div style={S.dateDay}>{fmtDay(d)}</div>
      <div style={S.dateMon}>{fmtMon(d)}</div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div style={S.row}>
    <span style={{ color: "#6b7280" }}>{k}</span>
    <span style={{ textAlign: "right", fontWeight: 500 }}>{v}</span>
  </div>
);

function ViewModal({ b, onClose }) {
  const st = STATUS[b.bookingStatus] || STATUS.COMPLETED;
  const discount = Number(b.discountAmount || 0) + Number(b.couponDiscount || 0);
  const ci = b.checkInDetails || {};
  const cn = b.cancellation || {};

  return (
    <Overlay onClose={onClose} wide>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={S.modalTitle}>#{b.bookingNumber}</h3>
        <button onClick={onClose} style={S.toastX} aria-label="Close">✕</button>
      </div>
      <div style={{ display: "flex", gap: 8, margin: "10px 0 4px", flexWrap: "wrap" }}>
        <span style={{ ...S.badge, background: st.bg, color: st.c }}>{st.label}</span>
        <span style={{ ...S.pay, ...(String(b.paymentStatus) === "PAID" ? S.payPaid : {}), marginTop: 0 }}>
          {pretty(b.paymentStatus)}
        </span>
      </div>

      <h4 style={S.sh}>Guest</h4>
      <Row k="Name" v={b.guest?.name || "—"} />
      <Row k="Phone" v={b.guest?.phone || "—"} />
      {b.guest?.email && <Row k="Email" v={b.guest.email} />}
      <Row k="Guests" v={`${b.guest?.adults || 1} adult(s), ${b.guest?.children || 0} child(ren)`} />
      {b.guest?.specialRequest && <Row k="Request" v={b.guest.specialRequest} />}

      <h4 style={S.sh}>Stay</h4>
      <Row k="Hotel" v={b.hotel?.hotelName || "—"} />
      <Row k="Room" v={`${b.room?.roomName || b.roomName || "—"} × ${b.roomsBooked}`} />
      <Row k="Check-in" v={fmtFull(b.checkIn)} />
      <Row k="Check-out" v={fmtFull(b.checkOut)} />
      <Row k="Nights" v={b.nights} />
      <Row k="Source" v={pretty(b.source)} />
      <Row k="Booked on" v={fmtDateTime(b.createdAt)} />

      <h4 style={S.sh}>Amount</h4>
      <Row k="Room amount" v={money(b.roomAmount)} />
      {b.extraGuestAmount > 0 && <Row k="Extra guest" v={money(b.extraGuestAmount)} />}
      {b.mealAmount > 0 && <Row k="Meals" v={money(b.mealAmount)} />}
      <Row k="Tax" v={money(b.taxAmount)} />
      <Row k="Service charge" v={money(b.serviceCharge)} />
      {discount > 0 && <Row k={`Discount ${b.couponCode || ""}`} v={`- ${money(discount)}`} />}
      <Row k="Total" v={<strong>{money(b.totalAmount)}</strong>} />
      <Row k={`Commission (${b.commissionPercentage || 0}%)`} v={money(b.adminCommission)} />
      <Row k="Your earning" v={<strong>{money(b.vendorAmount)}</strong>} />
      <Row k="Settlement" v={pretty(b.settlementStatus)} />

      <h4 style={S.sh}>Payment</h4>
      <Row k="Method" v={pretty(b.paymentMethod)} />
      {b.paidAt && <Row k="Paid at" v={fmtDateTime(b.paidAt)} />}
      {b.transactionId && <Row k="Transaction ID" v={b.transactionId} />}

      {ci.actualCheckIn && (
        <>
          <h4 style={S.sh}>Check-in details</h4>
          <Row k="Checked in" v={fmtDateTime(ci.actualCheckIn)} />
          {ci.actualCheckOut && <Row k="Checked out" v={fmtDateTime(ci.actualCheckOut)} />}
          <Row k="ID" v={`${ci.idType || "—"} ${ci.idNumber || ""} ${ci.idVerified ? "✔" : "(unverified)"}`} />
          {ci.remarks && <Row k="Remarks" v={ci.remarks} />}
        </>
      )}

      {cn.cancelled && (
        <>
          <h4 style={S.sh}>Cancellation</h4>
          <Row k="By" v={pretty(cn.cancelledBy)} />
          <Row k="At" v={fmtDateTime(cn.cancelledAt)} />
          <Row k="Reason" v={cn.reason || "—"} />
          <Row k="Refund" v={money(cn.refundAmount)} />
        </>
      )}
    </Overlay>
  );
}

/* ============================================================
   STYLES
============================================================ */
const S = {
  page: { maxWidth: 1200, margin: "0 auto", padding: "32px 24px 48px", fontFamily: "'DM Sans', sans-serif", background: "#f8fafc", minHeight: "100vh", color: "#111827" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: "#6b7280", marginTop: 6 },
  ghostBtn: { padding: "10px 18px", background: "white", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit" },

  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 20 },
  tabs: { display: "flex", gap: 6, overflowX: "auto", background: "white", padding: 6, borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,.06)", maxWidth: "100%" },
  tab: { display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "none", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 500, color: "#4b5563", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  tabOn: { background: "#166534", color: "white", fontWeight: 600 },
  count: { fontSize: 12, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: "#f3f4f6", color: "#6b7280" },
  countOn: { background: "rgba(255,255,255,.2)", color: "white" },
  countHot: { background: "#f59e0b", color: "white" },
  select: { padding: "11px 14px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "white", minWidth: 160 },
  search: { flex: "1 1 240px", maxWidth: 340, padding: "11px 16px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "white", boxSizing: "border-box" },
  note: { marginBottom: 14, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, fontSize: 13, color: "#92400e" },

  card: { background: "white", borderRadius: 14, padding: "20px 22px 16px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  dates: { display: "flex", alignItems: "center", gap: 10 },
  dateBlock: { width: 62, textAlign: "center", background: "#f8fafc", border: "1px solid #eef0f3", borderRadius: 10, padding: "7px 0" },
  dateWeek: { fontSize: 11, color: "#9ca3af", fontWeight: 600 },
  dateDay: { fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, lineHeight: 1.1 },
  dateMon: { fontSize: 12, color: "#6b7280", fontWeight: 600 },
  nightsLine: { flex: 1, minWidth: 18, position: "relative", display: "flex", justifyContent: "center", borderTop: "2px dashed #d1d5db" },
  nightsText: { position: "absolute", top: -11, background: "white", padding: "0 4px", fontSize: 11, fontWeight: 700, color: "#166534" },
  guest: { fontSize: 17, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  phone: { display: "inline-block", fontSize: 13, color: "#166534", fontWeight: 600, textDecoration: "none", margin: "2px 0 8px" },
  prop: { fontSize: 14, fontWeight: 600, color: "#374151" },
  muted: { fontSize: 12.5, color: "#9ca3af", textTransform: "capitalize" },
  id: { fontSize: 12, color: "#9ca3af", marginTop: 6, fontVariantNumeric: "tabular-nums" },
  metaRow: { fontSize: 14, color: "#374151", marginBottom: 4 },
  amount: { fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "#166534" },
  pay: { display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#fef3c7", color: "#b45309", textTransform: "capitalize" },
  payPaid: { background: "#dcfce7", color: "#166534" },
  foot: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 16, paddingTop: 14, borderTop: "1px solid #f3f4f6" },
  badge: { fontSize: 12.5, fontWeight: 700, padding: "5px 12px", borderRadius: 20, display: "inline-block" },
  when: { fontSize: 13, color: "#6b7280", fontWeight: 500 },
  whenHot: { color: "#b45309", fontWeight: 700 },
  btn: { padding: "9px 18px", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", border: "1.5px solid transparent" },
  btnPrimary: { background: "#166534", color: "white" },
  btnGhost: { background: "white", color: "#374151", borderColor: "#e5e7eb" },
  btnAmber: { background: "#fffbeb", color: "#92400e", borderColor: "#fde68a" },
  btnDanger: { background: "white", color: "#b91c1c", borderColor: "#fecaca" },
  btnDangerSolid: { background: "#dc2626", color: "white" },

  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 260 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "hbkspin 1s linear infinite" },
  empty: { textAlign: "center", padding: "64px 20px", background: "white", borderRadius: 16, boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  emptyIcon: { width: 56, height: 56, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 16, background: "#f0fdf4", color: "#166534", fontSize: 24 },
  toast: (bg) => ({ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "14px 20px", maxWidth: 420, background: bg, color: "white", borderRadius: 10, fontSize: 14, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,.18)", display: "flex", alignItems: "center", gap: 8 }),
  toastX: { background: "none", border: "none", color: "inherit", cursor: "pointer", marginLeft: 8, fontSize: 16 },

  overlay: { position: "fixed", inset: 0, background: "rgba(17,24,39,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: { background: "white", borderRadius: 18, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 26, boxShadow: "0 25px 80px rgba(0,0,0,.25)" },
  modalTitle: { fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, margin: 0 },
  modalFoot: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 },
  warn: { marginBottom: 12, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, fontSize: 13, color: "#92400e" },
  chip: { padding: "7px 13px", border: "1.5px solid #e5e7eb", borderRadius: 20, background: "white", fontSize: 13, color: "#4b5563", cursor: "pointer", fontFamily: "inherit" },
  chipOn: { background: "#166534", color: "white", borderColor: "#166534" },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
  input: { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "white", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#374151", margin: "12px 0" },
  sh: { margin: "18px 0 8px", fontSize: 14, fontWeight: 700, color: "#1e293b", borderBottom: "1px solid #e5e7eb", paddingBottom: 4 },
  row: { display: "flex", justifyContent: "space-between", gap: 14, padding: "4px 0", fontSize: 13.5 },
};